import {useState} from "react";
import {MultiFanSituationInputParameters} from "./multiFanNoise";
import {Situation} from "./Situation";

export interface MultiFanCriteria {
    situation: Situation | null;
    svg: string | null;
    error: string;
    totalFanCount: number;
}

export type MultiFanFormData = {
    fanPwlDba:  string;
    additionalUserGivenFanPwlDba: string;
    fanColumnCountInCooler:  string;
    fanRowCountInCooler:  string;
    coolerWidthMeters:  string;
    coolerHeightMeters:  string;
    coolerZCoordinate:  string;
    fanCaseDiameterMeters?:  string;
    totalCoolerColumnCount:  string;
    totalCoolerRowCount:  string;
    xDistanceBetweebCoolers:  string;
    yDistanceBetweebCoolers:  string;
    observerPosition: { xMeters:  string; yMeters:  string; zMeters:  string };
};

const defaultFormData: MultiFanFormData = {
    fanPwlDba: "88",
    additionalUserGivenFanPwlDba: "0",
    fanColumnCountInCooler: "3",
    fanRowCountInCooler: "2",
    coolerWidthMeters: "6",
    coolerHeightMeters: "4",
    coolerZCoordinate: "2",
    fanCaseDiameterMeters: "1.7",
    totalCoolerColumnCount: "2",
    totalCoolerRowCount: "3",
    xDistanceBetweebCoolers: "2",
    yDistanceBetweebCoolers: "3",
    observerPosition: {xMeters: "11", yMeters: "0", zMeters: "0"}
};

export function useMultiFanCriteriaForm() {
    const [formData, setFormData] = useState<MultiFanFormData>(defaultFormData);

    const [error, setError] = useState<string>("");
    const [svg, setSvg] = useState<string | null>(null);
    const [situation, setSituation] = useState<Situation | null>(null);

    const validateParameters = (params: MultiFanFormData): MultiFanSituationInputParameters => {
        const parseNumber = (value: number | string, fieldName: string): number => {
            if (typeof value === 'number') {
                return value;
            }
            const parsed = parseFloat(value);
            if (isNaN(parsed)) {
                throw new Error(`${fieldName} musi być liczbą (wpisano: "${value}")`);
            }
            return parsed;
        };

        const fanPwlDba = parseNumber(params.fanPwlDba, "fanPwlDba");
        if (fanPwlDba < 0) {
            throw new Error("fanPwlDba musi być dodatnią liczbą");
        }

        const additionalUserGivenFanPwlDba = parseNumber(params.additionalUserGivenFanPwlDba, "additionalUserGivenFanPwlDba");

        const fanColumnCountInCooler = parseNumber(params.fanColumnCountInCooler, "fanColumnCountInCooler");
        if (fanColumnCountInCooler < 1) {
            throw new Error("fanColumnCountInCooler musi być co najmniej 1");
        }

        const fanRowCountInCooler = parseNumber(params.fanRowCountInCooler, "fanRowCountInCooler");
        if (fanRowCountInCooler < 1) {
            throw new Error("fanRowCountInCooler musi być co najmniej 1");
        }

        const coolerWidthMeters = parseNumber(params.coolerWidthMeters, "coolerWidthMeters");
        if (coolerWidthMeters <= 0) {
            throw new Error("coolerWidthMeters musi być dodatnia");
        }

        const coolerHeightMeters = parseNumber(params.coolerHeightMeters, "coolerHeightMeters");
        if (coolerHeightMeters <= 0) {
            throw new Error("coolerHeightMeters musi być dodatnia");
        }

        const coolerZCoordinate = parseNumber(params.coolerZCoordinate, "coolerZCoordinate");

        const totalCoolerColumnCount = parseNumber(params.totalCoolerColumnCount, "totalCoolerColumnCount");
        if (totalCoolerColumnCount < 1) {
            throw new Error("totalCoolerColumnCount musi być co najmniej 1");
        }

        const totalCoolerRowCount = parseNumber(params.totalCoolerRowCount, "totalCoolerRowCount");
        if (totalCoolerRowCount < 1) {
            throw new Error("totalCoolerRowCount musi być co najmniej 1");
        }

        const xDistanceBetweenCoolers = parseNumber(params.xDistanceBetweebCoolers, "xDistanceBetweebCoolers");
        if (xDistanceBetweenCoolers < 0) {
            throw new Error("xDistanceBetweebCoolers musi być >= 0");
        }

        const yDistanceBetweenCoolers = parseNumber(params.yDistanceBetweebCoolers, "yDistanceBetweebCoolers");
        if (yDistanceBetweenCoolers < 0) {
            throw new Error("yDistanceBetweebCoolers musi być >= 0");
        }

        if (!params.observerPosition) {
            throw new Error("observerPosition jest wymagana");
        }

        // Parsowanie fanCaseDiameterMeters jeśli istnieje
        const fanCaseDiameterMeters = params.fanCaseDiameterMeters
            ? parseNumber(params.fanCaseDiameterMeters, "fanCaseDiameterMeters")
            : undefined;

        // Zwrócenie sparsowanych parametrów
        return {
            fanPwlDba,
            additionalUserGivenFanPwlDba,
            fanColumnCountInCooler,
            fanRowCountInCooler,
            coolerWidthMeters,
            coolerHeightMeters,
            coolerZCoordinate,
            fanCaseDiameterMeters,
            totalCoolerColumnCount,
            totalCoolerRowCount,
            xDistanceBetweenCoolers,
            yDistanceBetweenCoolers,
            observerPosition: {
                xMeters: parseFloat(params.observerPosition.xMeters + ""),
                yMeters: -parseFloat(params.observerPosition.yMeters + ""), // We reverrse Y-perception as typical non-IT user considers positive upright, negative downward
                zMeters: parseFloat(params.observerPosition.zMeters + ""),

            }
        };
    };

    const handleCalculate = () => {
        setError("");
        setSvg(null);

        try {
            // Waliduj i sparsuj parametry
            const validatedParams = validateParameters(formData);

            // Utwórz sytuację ze sparsowanych parametrów
            const newSituation = Situation.fromInputParameters(validatedParams);
            setSituation(newSituation);

            // Generuj SVG
            const svgHtml = newSituation.renderToSVG();
            setSvg(svgHtml);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : String(err);
            setError(`Błąd: ${errorMessage}`);
            setSvg(null);
            setSituation(null);
        }
    };

    const handleFieldChange = (field: keyof Omit<MultiFanFormData, 'observerPosition'>, value: string) => {
        setFormData(prev => ({...prev, [field]: value.replace(",", ".")}));
    };

    const handleObserverPositionChange = (axis: 'xMeters' | 'yMeters' | 'zMeters', value: string) => {
        setFormData(prev => ({
            ...prev,
            observerPosition: {
                ...prev.observerPosition,
                [axis]: value
            }
        }));
    };

    const totalFanCount = (() => {
        const parseToNumber = (value: number | string): number => {
            if (typeof value === 'number') return value;
            const parsed = parseFloat(value);
            return isNaN(parsed) ? 0 : parsed;
        };

        const totalCoolers = Math.round(parseToNumber(formData.totalCoolerColumnCount || 0) * parseToNumber(formData.totalCoolerRowCount || 0));
        const fansPerCooler = Math.round(parseToNumber(formData.fanColumnCountInCooler || 0) * parseToNumber(formData.fanRowCountInCooler || 0));
        return totalCoolers * fansPerCooler;
    })();

    const multiFanCriteria: MultiFanCriteria = {
        situation,
        svg,
        error,
        totalFanCount
    };

    const updateSvg = (newSvg: string) => {
        setSvg(newSvg);
    };

    return {
        multiFanCriteria,
        formData,
        handleFieldChange,
        handleObserverPositionChange,
        handleCalculate,
        updateSvg
    };
}
